import {Point, FeatureCollection} from "geojson"
import {Feature} from "ol"
import {GeoJSON} from "ol/format"
import {Point as OLPoint} from "ol/geom"
import {TBeaconProps} from "@ui/api/features"
import popupService from "./PopupService"
import {LayerId} from "./"
import FeatureService from "./FeatureService"
import aggregation from "./Aggregation"

export interface IBeaconService {
  addBeacons<T extends TBeaconProps>(buoys: FeatureCollection<Point, T>, layerId: LayerId): void
  toggleLayer(isVisible: boolean): void
}

export class BeaconService extends FeatureService implements IBeaconService {
  beaconLayers = [LayerId.BCNLAT, LayerId.BCNSPP]

  private constructBeconPopup(feature: Feature<OLPoint>) {
    const featureProps = feature.getProperties()
    return FeatureService.constructFeaturePopup([
      {
        name: "beacon",
        obj: featureProps,
        propNames: [
          "mark_type",
          "buoy_shape",
          "lateral_mark_category",
          "position",
          {name: "is_noticeable", isDictionary: true},
          {name: "obj_name", hasNational: true, isDictionary: true},
          {name: "information", hasNational: true, isDictionary: true}
        ]
      },
      ...(featureProps.aggregation_ids || []).map((aggr_id: number) => ({
          name: "gate",
          obj: aggregation.getAggregation(aggr_id),
          propNames: [{name: "obj_name", hasNational: true, isDictionary: true}, {name: "information", hasNational: true, isDictionary: true}]
      })),
      {
        name: "lights",
        obj: featureProps.lights,
        propNames: [
          "colour",
          "light_type",
          {name:"signal_group", isData: true},
          {name:"signal_period", isData: true}
        ]
      }
    ])
  }

  addBeacons<T extends TBeaconProps>(beacons: FeatureCollection<Point, T>, layerId: LayerId) {
    const formatter = new GeoJSON()
    let features = formatter.readFeatures(beacons) as Feature<OLPoint>[]
    if (features.length > 0) {
      features = features.map(feature => {
        feature.set("onClick", () => {
          const aggregation_ids = feature.getProperties().aggregation_ids
          popupService.showPopup(
            feature,
            this.constructBeconPopup(feature),
            aggregation_ids && aggregation_ids.length > 0
              ? aggregation.cancelAggregation.bind(aggregation)
              : () => feature.setProperties({selected: false})
          )
          if (aggregation_ids && aggregation_ids.length > 0) aggregation.selectAggregation(aggregation_ids)
          else feature.setProperties({selected: true})
        })
        return feature
      })
      FeatureService.addFeatures(layerId, features)
    }
  }

  toggleLayer(isVisible: boolean) {
    this.beaconLayers.forEach(layerId => FeatureService.getLayer(layerId)?.setVisible(isVisible))
  }
}

export default new BeaconService() as IBeaconService
